import I18n from 'i18n-js';
import qs from 'qs';
import { filter } from 'lodash';
import LocationUtil from '../../shared/location_util';

import { V3 } from '../../api_request';
import Suggestion, { ISuggestionData } from './suggestion';
import SuggestionCollection from './suggestion_collection';
import { CollectionHeaderStore } from '../stores/collection_header_store';
import { RECENT_SEARCH } from './site_search';
import UrlHelpers from '../../shared/url_helpers';

import SiteSearchSuggestion from './site_search_suggestion';

const MAX_MAIN_RESULTS = 5;
const MAX_SUB_RESULTS = 2;
const CURATED_SET_TYPE = 'curated_set';

export function buildRecentSearches(searches: string[]): SuggestionCollection {
  if (searches.length === 0) {
    return new SuggestionCollection([]);
  }

  const results = searches.map((value, idx) => {
    return new Suggestion({ value, type: RECENT_SEARCH, position: idx }, SiteSearchSuggestion);
  });

  return new SuggestionCollection([{
    results,
    heading: I18n.t('discovery.autocomplete.recentSearches'),
  }]);
}

export function addCurrentPageSuggestion(suggestions: Suggestion[], query: string): void {
  const { collectionHeader } = CollectionHeaderStore.getState();

  if (
    !collectionHeader ||
    (!LocationUtil.isOnHandpickedPage() && !LocationUtil.isOnSalePage())
  ) { return; }

  const { pathname, search, origin } = window.location;
  const currentQueryParams = qs.parse(search.slice(1));
  const params = qs.stringify({ ...currentQueryParams, query });
  const url = `${origin}${pathname}?${params}`;

  suggestions.unshift(
    new Suggestion(
      {
        url,
        filter_value: collectionHeader.title,
        filters: [],
        params: {},
        type: 'currentPage',
        value: query,
      },
      SiteSearchSuggestion,
    ),
  );
}

function setPositions(suggestions: Suggestion[], offset: number): void {
  suggestions.forEach((s, idx) => { s.position = idx + offset; });
}

export function buildSuggestions(
  suggestions: Partial<ISuggestionData[]>,
  query: string,
): SuggestionCollection {
  const allSuggestions = suggestions.reduce(
    (all, suggestion) => all.concat(new Suggestion(suggestion, SiteSearchSuggestion).filters),
    [],
  );

  const mainSuggestions = filter(allSuggestions, suggestion => (
    (suggestion.type !== CURATED_SET_TYPE)
  )).slice(0, MAX_MAIN_RESULTS);

  addCurrentPageSuggestion(mainSuggestions, query);

  const subSuggestions = filter(allSuggestions, s => (s.type === CURATED_SET_TYPE))
    .slice(0, MAX_SUB_RESULTS);

  setPositions(mainSuggestions, 0);
  setPositions(subSuggestions, mainSuggestions.length);

  const suggestionGroups = [
    {
      heading: null,
      results: mainSuggestions,
    },
    {
      heading: I18n.t('discovery.searchBar.context.handpickedCollections'),
      results: subSuggestions,
    },
  ].filter(group => group.results.length);

  return new SuggestionCollection(suggestionGroups);
}

export default class ReverbSiteSearchSuggestions {
  static fetch(query: string) {
    return V3.get(UrlHelpers.autocompletePath(query)).then((suggestions: ISuggestionData[]) => {
      return buildSuggestions(suggestions, query);
    });
  }
}
